// Shared visual language for the odontogram.
//
// Tooth sets and surface rules are NOT hardcoded here — they come from
// GET /dental/meta so the frontend physically cannot drift from the backend's
// idea of which surfaces are valid on which tooth.
//
// Findings are never distinguished by colour alone: each carries a letter glyph
// and, where it matters, a fill pattern. Charts are read at a glance under
// operatory lighting, and roughly 8% of male dentists have a colour-vision
// deficiency.

export const FINDING_STYLE = {
  sound:           { glyph: "",   fill: "#ffffff", stroke: "#94a3b8", text: "#0f172a", label: "Sound" },
  caries:          { glyph: "C",  fill: "#dc2626", stroke: "#991b1b", text: "#ffffff", label: "Caries" },
  restoration:     { glyph: "R",  fill: "#2563eb", stroke: "#1e40af", text: "#ffffff", label: "Restoration" },
  crown:           { glyph: "Cr", fill: "#f59e0b", stroke: "#b45309", text: "#1f2937", label: "Crown" },
  root_canal:      { glyph: "RC", fill: "#7c3aed", stroke: "#5b21b6", text: "#ffffff", label: "Root canal" },
  sealant:         { glyph: "S",  fill: "#14b8a6", stroke: "#0f766e", text: "#ffffff", label: "Sealant" },
  fracture:        { glyph: "F",  fill: "#fb7185", stroke: "#9f1239", text: "#1f2937", label: "Fracture" },
  missing:         { glyph: "X",  fill: "#e2e8f0", stroke: "#64748b", text: "#334155", label: "Missing" },
  extracted:       { glyph: "X",  fill: "#e2e8f0", stroke: "#64748b", text: "#334155", label: "Extracted" },
  implant:         { glyph: "I",  fill: "#0f172a", stroke: "#0f172a", text: "#ffffff", label: "Implant" },
  bridge_pontic:   { glyph: "P",  fill: "#a16207", stroke: "#713f12", text: "#ffffff", label: "Bridge pontic" },
  bridge_abutment: { glyph: "A",  fill: "#ca8a04", stroke: "#713f12", text: "#1f2937", label: "Bridge abutment" },
  mobility:        { glyph: "M",  fill: "#f97316", stroke: "#9a3412", text: "#ffffff", label: "Mobility" },
  impacted:        { glyph: "IM", fill: "#64748b", stroke: "#334155", text: "#ffffff", label: "Impacted" },
  unerupted:       { glyph: "U",  fill: "#cbd5e1", stroke: "#64748b", text: "#334155", label: "Unerupted" },
  watch:           { glyph: "W",  fill: "#fde047", stroke: "#a16207", text: "#1f2937", label: "Watch" },
};

export const DEFAULT_STYLE = { glyph: "?", fill: "#f1f5f9", stroke: "#94a3b8", text: "#0f172a", label: "Unknown" };

export function styleFor(findingType) {
  return FINDING_STYLE[findingType] || DEFAULT_STYLE;
}

// Planned work is drawn hollow so a treatment plan never looks like completed
// work on the chart — the single most dangerous confusion in a dental record.
export function isPlanned(mark) {
  return mark?.status === "planned";
}

// Grid layout: two rows, quadrants read outwards from the midline exactly as a
// paper chart does. Deliberately NOT an anatomical arch — the grid is what
// dentists already read, and it scales to any tablet width without media queries.
export const UPPER_RIGHT = ["18", "17", "16", "15", "14", "13", "12", "11"];
export const UPPER_LEFT  = ["21", "22", "23", "24", "25", "26", "27", "28"];
export const LOWER_RIGHT = ["48", "47", "46", "45", "44", "43", "42", "41"];
export const LOWER_LEFT  = ["31", "32", "33", "34", "35", "36", "37", "38"];

export const UPPER_RIGHT_PRIMARY = ["55", "54", "53", "52", "51"];
export const UPPER_LEFT_PRIMARY  = ["61", "62", "63", "64", "65"];
export const LOWER_RIGHT_PRIMARY = ["85", "84", "83", "82", "81"];
export const LOWER_LEFT_PRIMARY  = ["71", "72", "73", "74", "75"];

export function rowsFor(dentition) {
  if (dentition === "primary") {
    return {
      upper: [...UPPER_RIGHT_PRIMARY, ...UPPER_LEFT_PRIMARY],
      lower: [...LOWER_RIGHT_PRIMARY, ...LOWER_LEFT_PRIMARY],
    };
  }
  if (dentition === "mixed") {
    return {
      upper: [...UPPER_RIGHT, ...UPPER_LEFT],
      lower: [...LOWER_RIGHT, ...LOWER_LEFT],
      upperPrimary: [...UPPER_RIGHT_PRIMARY, ...UPPER_LEFT_PRIMARY],
      lowerPrimary: [...LOWER_RIGHT_PRIMARY, ...LOWER_LEFT_PRIMARY],
    };
  }
  return {
    upper: [...UPPER_RIGHT, ...UPPER_LEFT],
    lower: [...LOWER_RIGHT, ...LOWER_LEFT],
  };
}

/** Human summary of one tooth's charted state, for lists and tooltips. */
export function summariseTooth(entry) {
  if (!entry) return "";
  if (entry.whole) return styleFor(entry.whole.finding_type).label;
  const s = entry.surfaces || {};
  const keys = Object.keys(s);
  if (!keys.length) return "";
  const byType = {};
  keys.forEach((k) => {
    const t = s[k].finding_type;
    (byType[t] = byType[t] || []).push(k);
  });
  return Object.entries(byType)
    .map(([t, surfaces]) => `${styleFor(t).label} (${surfaces.sort().join("")})`)
    .join(", ");
}
